/**
 * Classifying a saved environment runtime as a control surface (a device that
 * merely CONNECTS — browser, mobile) vs. one that can actually EXECUTE work.
 *
 * Extracted from `buildRuntimeProjectGroups.ts`, which grew past its own cap:
 * this is a self-contained decision tree over one input shape
 * (`SavedEnvironmentRuntimeEntry`) with no dependency on the runtime-grouping
 * logic that stayed behind, and only `isExecutableRuntimeEntry` crosses back
 * out (consumed by `getSavedExecutableEnvironmentIds`).
 */
export type SavedEnvironmentRuntimeEntry =
  | {
      readonly role?: string | null;
      readonly kind?: string | null;
      readonly descriptor?: {
        readonly label?: string;
        readonly platform?: { readonly os?: string | null } | null;
        readonly client?: {
          readonly deviceType?: string | null;
          readonly platform?: string | null;
        } | null;
        readonly capabilities?: {
          readonly runtime?: { readonly executionPlane?: boolean } | undefined;
          readonly projects?: { readonly localProjects?: boolean } | undefined;
          readonly tools?:
            | {
                readonly agents?: boolean;
                readonly terminal?: boolean;
              }
            | undefined;
        };
      } | null;
    }
  | undefined;

const CONTROL_SURFACE_RUNTIME_KINDS = new Set(["browser", "web", "mobile", "ios", "android"]);

function isControlSurfaceRuntimeEntry(runtimeState: SavedEnvironmentRuntimeEntry): boolean {
  if (!runtimeState?.descriptor) return true;
  if (runtimeState.role === "client") return true;
  const runtimeKind = runtimeState.kind?.trim().toLowerCase();
  const clientDeviceType = runtimeState.descriptor.client?.deviceType?.trim().toLowerCase();
  const clientPlatform = runtimeState.descriptor.client?.platform?.trim().toLowerCase();
  const platformOs = runtimeState.descriptor.platform?.os?.trim().toLowerCase();
  return [runtimeKind, clientDeviceType, clientPlatform, platformOs].some(
    (value) => value !== undefined && value !== "" && CONTROL_SURFACE_RUNTIME_KINDS.has(value),
  );
}

function runtimeCapabilitiesAllowExecution(
  capabilities: NonNullable<
    NonNullable<SavedEnvironmentRuntimeEntry>["descriptor"]
  >["capabilities"],
): boolean {
  const executionCapabilities = [
    capabilities?.runtime?.executionPlane,
    capabilities?.projects?.localProjects,
    capabilities?.tools?.agents,
    capabilities?.tools?.terminal,
  ];
  return executionCapabilities.some((capability) => capability === true);
}

export function isExecutableRuntimeEntry(runtimeState: SavedEnvironmentRuntimeEntry): boolean {
  if (!runtimeState?.descriptor) return false;
  if (isControlSurfaceRuntimeEntry(runtimeState)) return false;
  return runtimeCapabilitiesAllowExecution(runtimeState.descriptor.capabilities);
}
